import React from 'react'

import { sumBy } from 'lodash'

import BillsHeader from './common/BillsHeader'

import { ModelBaseModel } from '../../../models/model-base/model-base.model'
import { IRegistrationFeesInterface } from '../../../models/registration-fees/registrationFees.interface'
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../../types/payment'
import { formatDateWithTime } from '../../../helpers/time'
import { getFloatPrice } from '../../../helpers/price'

type IDetailRow = {
  item: IRegistrationFeesInterface
  date: string
  showRefund: boolean
  currency: string
}

type ITotalRow = {
  numbersOfUnit: number
  price: string
  modalThemeColor: `#${string}`
  scratchedTotal: number
  refundTotal: number
  showRefund: boolean
  refundFeeTotalState: number
  currency: string
}

type Props = {
  data: IRegistrationFeesInterface[]
  modalThemeColor: `#${string}`
  showRefund: boolean
  refundFeeTotalState: number
  currency: string
}

type IEventReviewPublishInput = {
  className?: string
  data?: any
  price?: any
  onClick?: any
  moreBtn?: boolean
  icon?: string
  postIcon?: string
  style?: React.CSSProperties
}

const EventReviewPublishInput: React.FC<IEventReviewPublishInput> = (args) => {
  const { className, data, price, onClick, moreBtn, icon, postIcon, style = {} } = args

  return (
    <>
      {onClick ? (
        <div
          style={style}
          onClick={onClick}
          className={`${className} text-SeabiscuitDark200ThemeColor`}
        >
          <div className="text-SeabiscuitDark200ThemeColor mr-1 font-normal">{price}</div>
          <div>{data}</div>
          {moreBtn ? <span className="cursor-pointer underline">more</span> : null}
        </div>
      ) : (
        <div className={`${className} text-SeabiscuitDark200ThemeColor`} style={style}>
          {icon ? <img className="w-5 h-5 absolute left-3" src={icon} alt="icon" /> : null}
          <div className="text-SeabiscuitDark200ThemeColor mr-1 font-normal">{price}</div>
          <div>{data}</div>
          {postIcon ? <img className="w-5 h-5 absolute right-3" src={postIcon} alt="icon" /> : null}
          {moreBtn ? (
            <span className="text-SeabiscuitDark200ThemeColor cursor-pointer underline">more</span>
          ) : null}
        </div>
      )}
    </>
  )
}

const DetailRow = ({ item, date, showRefund, currency }: IDetailRow) => {
  return (
    <div className="flex flex-col md:flex-row md:items-start w-full">
      <EventReviewPublishInput
        className={`rounded-lg h-12 bg-SeabiscuitGrayThemeColor text-sm p-3 m-1 ml-0 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'}`}
        data={item.feesTitle}
      />
      <EventReviewPublishInput
        className={`rounded-lg text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor text-sm p-3 m-1 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'}`}
        data={date}
      />
      <EventReviewPublishInput
        className={`rounded-lg bg-SeabiscuitGrayThemeColor relative flex items-center justify-center text-sm p-3 m-1 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'} `}
        data={item.selectedUnitsCount}
      />
      <EventReviewPublishInput
        className={`rounded-lg bg-SeabiscuitGrayThemeColor relative flex items-center justify-center text-sm p-3 m-1 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'} `}
        data={getFloatPrice(`${currency}${item.feesPrice}`)}
        icon={
          item.paymentType === PAYMENT_TYPE.CARD
            ? '/assets/img/dark/credit-card.svg'
            : item.paymentType === PAYMENT_TYPE.CHECK
              ? '/assets/img/dark/check.svg'
              : '/assets/img/dark/cash.svg'
        }
        postIcon={
          item.paymentStatus === PAYMENT_STATUS.PAID
            ? '/assets/cp_icons/Ok-3.svg'
            : '/assets/cp_icons/Cancel-2.svg'
        }
      />
      {showRefund && (
        <>
          {' '}
          <EventReviewPublishInput
            className="rounded-lg bg-SeabiscuitGrayThemeColor relative flex items-center justify-center text-sm p-3 m-1 w-full md:w-3/12"
            data={`${item.amountScratched > 0 ? 'Scratched' : 'No'}`}
          />
          <EventReviewPublishInput
            className="rounded-lg bg-SeabiscuitGrayThemeColor relative flex items-center justify-center text-sm p-3 m-1 w-full md:w-3/12"
            data={getFloatPrice(`${currency}${item.amountRefunded}`)}
          />
        </>
      )}
    </div>
  )
}

const TotalRow = (args: ITotalRow) => {
  const {
    numbersOfUnit,
    price,
    modalThemeColor,
    scratchedTotal,
    showRefund,
    refundFeeTotalState,
    currency,
  } = args

  return (
    <>
      {showRefund ? (
        <div className="flex flex-col md:flex-row md:items-start w-full">
          <EventReviewPublishInput
            className={
              'rounded-lg text-[#122B46] text-sm p-3 m-1 w-full md:w-[21%] font-semibold bg-SeabiscuitGreenLightThemeColor'
            }
            // style={{
            //     backgroundColor: args.modalThemeColor
            // }}
            data={`Total`}
          />
          <EventReviewPublishInput
            className="rounded-lg text-[#122B46] text-sm p-3 m-1 w-full md:w-10/12 font-semibold flex md:justify-end bg-SeabiscuitGreenLightThemeColor"
            price={`${getFloatPrice(`${currency}${price}`)} paid, `}
            data={`${getFloatPrice(`${currency}${refundFeeTotalState}`)} refunded`}
            // style={{
            //     backgroundColor: args.modalThemeColor
            // }}
          />
        </div>
      ) : (
        <div className="flex flex-col md:flex-row md:items-start w-full">
          <EventReviewPublishInput
            className={`rounded-lg bg-[#00B6AA1A] text-sm p-3 m-1 ml-0 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'} font-semibold`}
            data={`Total`}
            style={{
              background: modalThemeColor,
            }}
          />
          <EventReviewPublishInput
            className={`rounded-lg bg-[#00B6AA1A] relative flex items-center justify-center text-sm p-3 m-1 ml-0 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'} font-semibold`}
            data={`${numbersOfUnit} fees`}
            style={{
              background: modalThemeColor,
            }}
          />
          <EventReviewPublishInput
            className={`rounded-lg bg-[#00B6AA1A] relative flex items-center justify-center text-sm p-3 m-1 ml-0 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'} font-semibold`}
            data={getFloatPrice(`${currency}${price}`)}
            style={{
              background: modalThemeColor,
            }}
          />
          {showRefund && (
            <>
              <EventReviewPublishInput
                className="rounded-lg bg-[#00B6AA1A] relative flex items-center justify-center text-sm p-3 m-1 ml-0 w-3/12 font-semibold"
                data={getFloatPrice(`${currency}${scratchedTotal}`)}
                style={{
                  background: modalThemeColor,
                }}
              />
              <EventReviewPublishInput
                className="rounded-lg bg-[#00B6AA1A] relative flex items-center justify-center text-sm p-3 m-1 ml-0 w-3/12 font-semibold"
                data={getFloatPrice(`${currency}${scratchedTotal}`)}
                style={{
                  background: modalThemeColor,
                }}
              />
            </>
          )}
        </div>
      )}
    </>
  )
}

const BillDetailsFeesData = (props: Props) => {
  let feestotal = 0
  const { refundFeeTotalState, currency } = props

  const selectedUnitsCount = sumBy(props?.data, 'selectedUnitsCount')

  return (
    <>
      {selectedUnitsCount ? (
        <div className="flex items-center">
          <BillsHeader title="Item" tooltipText="Line item" />
          <BillsHeader title="Date" tooltipText="Payed Date" />
          <BillsHeader title="Units" tooltipText="Number of units" />
          <BillsHeader title="Price per unit" tooltipText="Price per unit" />
          {props.showRefund && (
            <>
              <BillsHeader title="Scratched?" tooltipText="Scratched" />
              <BillsHeader title="Refund" tooltipText="Refund" />
            </>
          )}
        </div>
      ) : null}

      {selectedUnitsCount ? (
        props?.data.map((item, index: number) => {
          if (item.paymentStatus === PAYMENT_STATUS.PAID)
            feestotal += item.feesPrice * item.selectedUnitsCount
          return (
            <DetailRow
              key={index}
              item={item}
              date={formatDateWithTime(item?.created as Date)}
              showRefund={props.showRefund}
              currency={currency}
            />
          )
        })
      ) : (
        <span className="w-full text-sm text-SeabiscuitLightParagraphTextColor text-center">
          No fees added
        </span>
      )}

      {selectedUnitsCount ? (
        <TotalRow
          modalThemeColor={props.modalThemeColor}
          numbersOfUnit={
            Array.isArray(props?.data)
              ? props?.data.reduce((accumulator, currentValue) => {
                  return +accumulator + +currentValue.selectedUnitsCount
                }, 0)
              : 0
          }
          price={new ModelBaseModel().getNum(feestotal).toString()}
          scratchedTotal={0}
          refundTotal={0}
          showRefund={props.showRefund}
          refundFeeTotalState={refundFeeTotalState}
          currency={currency}
        />
      ) : null}
    </>
  )
}

export default BillDetailsFeesData
